import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs/operators';
import { SaleService } from 'src/app/core/services/sale.service';
import { ProductService } from 'src/app/core/services/product.service';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-walking-sale',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './walking-sale.component.html',
  styleUrls: ['./walking-sale.component.scss']
})
export class WalkingSaleComponent {

  saleForm!: FormGroup;
  products: any[] = [];
  loading = false;
  error: string | null = null;
  currentTime: string = '';

  currentPage = 1;
  pageLimit = 20;
  totalPages = 1;
  productInput$ = new Subject<string>();
  searchTerm = '';

  constructor(
    private fb: FormBuilder,
    private saleService: SaleService,
    private productService: ProductService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initForm();

    this.updateClock();
    setInterval(() => this.updateClock(), 1000);

    // ✅ Initial load first page products
    this.currentPage = 1;
    this.searchTerm = '';
    this.loadProducts();

    // Typeahead search
    this.productInput$
      .pipe(
        debounceTime(300),
        switchMap(term => {
          this.searchTerm = term;
          this.currentPage = 1;
          this.products = [];
          this.loading = true;
          return this.productService.getProducts({ search: term, page: this.currentPage, limit: this.pageLimit })
            .pipe(finalize(() => this.loading = false));
        })
      )
      .subscribe((res: any) => {
        this.products = res.data.products;
        this.totalPages = res.data.pages;
      });
  }

  updateClock() {
    const now = new Date();
    this.currentTime = now.toLocaleTimeString();
  }

  // ✅ Initialize reactive form
  initForm() {
    this.saleForm = this.fb.group({
      saleType: ['walkin', Validators.required],
      saleDate: [new Date(), Validators.required],
      note: [''],
      items: this.fb.array([]),
      subTotal: [0],
      totalDiscount: [0],
      totalTax: [0],
      grandTotal: [0],
      labourExpense: [0],
      otherExpense: [0],
      freightExpense: [0],
      payments: this.fb.array([]),
      totalPaid: [0],
      remainingBalance: [0],
      paymentStatus: ['unpaid', Validators.required],
    });
  }

  // ✅ Form getters
  get items(): FormArray {
    return this.saleForm.get('items') as FormArray;
  }

  get payments(): FormArray {
    return this.saleForm.get('payments') as FormArray;
  }

  addPayment(payment?: any) {
    this.payments.push(this.fb.group({
      amount: [payment?.amount || 0, Validators.required],
      method: [payment?.method || 'cash', Validators.required],
      note: [payment?.note || ''],
      date: [payment?.date ? new Date(payment.date) : new Date(), Validators.required],
      status: [payment?.status || 'unpaid'],
      paymentStatus: [payment?.paymentStatus || 'unpaid'] // ✅ Add this
    }));
    this.updateTotals(); // update totals after adding payment
  }


  removePayment(index: number) {
    this.payments.removeAt(index);
    this.updateTotals(); // ✅ add this
  }

  // ------------------ Loaders ------------------

  loadProducts(loadMore = false) {
    if (!loadMore) {
      this.currentPage = 1; // initial load page 1
      this.products = [];   // reset products
    }

    this.loading = true;
    this.productService.getProducts({ search: this.searchTerm, page: this.currentPage, limit: this.pageLimit })
      .pipe(finalize(() => this.loading = false))
      .subscribe((res: any) => {
        if (loadMore) {
          this.products = [...this.products, ...res.data.products]; // append next page
        } else {
          this.products = res.data.products; // first page load
        }
        this.totalPages = res.data.pages;
      });
  }


  scrollProducts() {
    if (this.currentPage >= this.totalPages || this.loading) return;

    this.currentPage++;
    this.loading = true;
    this.productService.getProducts({ search: this.searchTerm, page: this.currentPage, limit: this.pageLimit })
      .pipe(finalize(() => this.loading = false))
      .subscribe((res: any) => {
        this.products = [...this.products, ...res.data.products];
        this.totalPages = res.data.pages;
      });
  }

  onProductSelected(selected: any) {
    if (!selected) return;

    // 🧠 Case 1: if full product object received
    const product = selected._id ? selected : this.products.find(p => p._id === selected);

    if (!product) {
      console.warn('⚠️ Product not found:', selected);
      return;
    }

    // ✅ Duplicate check
    const alreadyAdded = this.items.controls.some(
      (ctrl: any) => ctrl.value.productId === product._id
    );
    if (alreadyAdded) {
      Swal.fire('نوٹ', 'یہ پراڈکٹ پہلے سے شامل ہے۔', 'info');
      return;
    }

    // ✅ Add product to form array
    this.items.push(
      this.fb.group({
        productId: [product._id, Validators.required],
        productName: [product.productName],
        quantity: [1, [Validators.required, Validators.min(1)]],
        unitPrice: [product.salePrice || 0, [Validators.required, Validators.min(0)]],
        discount: [product.discount || 0],
        tax: [product.taxRate || 0],
        total: [0],
      })
    );

    this.updateTotals();
  }

  // ------------------ Items ------------------

  addItem(productId: string) {
    const product = this.products.find(p => p._id === productId);
    if (!product) return;

    const itemGroup = this.fb.group({
      productId: [product._id, Validators.required],
      productName: [product.productName],
      quantity: [1, [Validators.required, Validators.min(1)]],
      unitPrice: [product.salePrice || 0, [Validators.required, Validators.min(0)]],
      discount: [product.discount || 0],
      tax: [product.taxRate || 0],
      total: [0]
    });

    this.items.push(itemGroup);
    this.updateTotals();
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.updateTotals();
  }

  updateTotals() {
    let subTotal = 0;

    this.items.controls.forEach(itemCtrl => {
      const val = itemCtrl.value;
      let total = val.unitPrice * val.quantity;
      if (val.discount) total -= (total * val.discount) / 100;
      if (val.tax) total += (total * val.tax) / 100;
      itemCtrl.patchValue({ total }, { emitEvent: false });
      subTotal += total;
    });

    const labourExpense = parseFloat(this.saleForm.get('labourExpense')?.value) || 0;
    const otherExpense = parseFloat(this.saleForm.get('otherExpense')?.value) || 0;
    const freightExpense = parseFloat(this.saleForm.get('freightExpense')?.value) || 0;

    const grandTotal = subTotal + labourExpense + otherExpense + freightExpense;

    const totalPaid = this.payments.controls.reduce((sum, payCtrl) => {
      const amt = parseFloat(payCtrl.get('amount')?.value) || 0;
      return sum + amt;
    }, 0);

    const remainingBalance = grandTotal - totalPaid;

    // ✅ Determine paymentStatus automatically
    let paymentStatus: 'paid' | 'partial' | 'unpaid' = 'unpaid';
    if (totalPaid === 0) paymentStatus = 'unpaid';
    else if (totalPaid >= grandTotal) paymentStatus = 'paid';
    else paymentStatus = 'partial';

    this.saleForm.patchValue({
      subTotal,
      grandTotal,
      totalPaid,
      remainingBalance,
      paymentStatus
    }, { emitEvent: false });
  }

  get totalPaid(): number {
    return this.payments.controls.reduce((sum, payCtrl) => {
      const amt = parseFloat(payCtrl.get('amount')?.value) || 0;
      return sum + amt;
    }, 0);
  }



  // ------------------ Submit ------------------

  submitSale() {
    console.log('submitSale called'); // <-- debug
    if (this.saleForm.invalid) {
      Swal.fire('غلطی', 'براہ کرم تمام مطلوبہ فیلڈز مکمل کریں۔', 'warning');
      return;
    }

    Swal.fire({
      title: 'کیا آپ یقیناً سیل بنانا چاہتے ہیں؟',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'ہاں، بنائیں',
      cancelButtonText: 'نہیں'
    }).then(result => {
      if (!result.isConfirmed) return;

      this.loading = true;

      // ⭐ Only saleType (walkin)
      const payload = {
        ...this.saleForm.value,
        saleType: "walkin"
      };

      this.saleService.createSale(payload)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: () => {
            Swal.fire('کامیابی', 'سیل کامیابی سے بن گئی۔', 'success');
            this.saleForm.reset();
            this.items.clear();
            this.router.navigate(['/']);
          },
          error: err => {
            console.error(err);
            Swal.fire('خرابی', err?.error?.message || 'سیل بناتے وقت مسئلہ پیش آیا۔', 'error');
          }
        });
    });
  }

}
